import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import {
  SegmentControls,
  SegmentControlsContent,
  SegmentControlsList,
  SegmentControlsTrigger
} from '~/components/common/SegmentControls'
import { SelectItem, Selector } from '~/components/common/Selector'
import { Typography } from '~/components/common/Typography'
import { UploadFile, UploadState as UploadFileState } from '~/components/common/UploadFile'
import { UsedCommandType } from '~/constants/commands'
import { CodeFileIcon, XmlFileUploadLeftIcon } from '~/constants/icon'
import { usePluginWorkspaceSafe } from '~/contexts/PluginWorkspaceContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useIndexes } from '~/hooks'
import { useFolderPicker } from '~/hooks/useFolderPicker'
import { useRepositoryBranches } from '~/hooks/useCodeContext'
import { bridge } from '~/services/bridge'
import { uploadFile as uploadOrgFile } from '~/services/organizationService'
import { projectService } from '~/services/plugin/projectService'
import { ProjectSelectionOption } from '~/store/taskFormSlice'
import { RepositoryBranch } from '~/types/Conversations'
import { createCompressedTarArchive } from '~/utils/tarUtils'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'

interface IntegrationRepoSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  command: UsedCommandType
  initialOption: ProjectSelectionOption
  repositories: RepositoryBranch[]
  initialS3Path?: string
  taskRuntimeSettings: Record<string, string | undefined>
  onSave: (payload: {
    option: ProjectSelectionOption
    repository?: RepositoryBranch
    runtimeSettings?: Record<string, string | undefined>
    s3Path?: string
  }) => void
  disabled?: boolean
}

type TabValue = ProjectSelectionOption | 'ide'

const defaultUploadState: UploadFileState = {
  status: 'default'
}

const buildRepositoryItemKey = (repo: { index_name: string; conn_type: string }) =>
  JSON.stringify({ index_name: repo.index_name, conn_type: repo.conn_type })

export function IntegrationRepoSelectionModal({
  isOpen,
  onClose,
  command,
  initialOption,
  repositories,
  initialS3Path,
  taskRuntimeSettings,
  onSave,
  disabled = false
}: IntegrationRepoSelectionModalProps) {
  const isInPlugin = bridge.isInPlugin()
  const pluginWs = usePluginWorkspaceSafe()

  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const tab = initialOption === 'upload' ? 'upload' : 'codeContext'
    return tab
  })
  const [selectedRepoKey, setSelectedRepoKey] = useState<string | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  const [pendingRuntimeSettings, setPendingRuntimeSettings] =
    useState<Record<string, string | undefined>>(taskRuntimeSettings)
  const [uploadState, setUploadState] = useState<UploadFileState>(defaultUploadState)
  const [uploadedPath, setUploadedPath] = useState<string>(initialS3Path || '')

  const [selectedIdePath, setSelectedIdePath] = useState<string | null>(null)
  const [ideUploadedPath, setIdeUploadedPath] = useState<string>('')
  const [isIdeUploading, setIsIdeUploading] = useState(false)

  const { currentWorkspace } = useWorkspaceContext()
  const { repos, getBranches } = useRepositoryBranches(command)
  const indexes = useIndexes({ commandType: command })

  // Prefill state whenever modal opens
  useEffect(() => {
    if (!isOpen) {
      return
    }
    setActiveTab(initialOption === 'upload' ? 'upload' : 'codeContext')
    setSelectedIdePath(null)
    setIdeUploadedPath('')
    setIsIdeUploading(false)
    const primaryRepo = repositories[0]
    if (primaryRepo) {
      setSelectedRepoKey(
        buildRepositoryItemKey({
          index_name: primaryRepo.repository,
          conn_type: primaryRepo.conn_type
        })
      )
      setSelectedBranch(primaryRepo.branch)
    } else {
      setSelectedRepoKey(null)
      setSelectedBranch('')
    }
    setPendingRuntimeSettings(taskRuntimeSettings)

    // Initialize upload state based on initialS3Path
    if (initialS3Path && initialOption === 'upload') {
      const fileName = initialS3Path.split('/').pop() || 'uploaded-project'
      setUploadState({
        status: 'uploaded',
        file: { name: fileName, size: 0 }
      })
      setUploadedPath(initialS3Path)
    } else {
      setUploadState(defaultUploadState)
      setUploadedPath('')
    }
  }, [initialOption, initialS3Path, isOpen, repositories, taskRuntimeSettings])

  const selectableRepos = useMemo(() => repos, [repos])

  const branchItems = useMemo(() => {
    if (!selectedRepoKey) {
      return []
    }
    try {
      const { index_name, conn_type } = JSON.parse(selectedRepoKey) as {
        index_name: string
        conn_type: string
      }
      return getBranches(conn_type, index_name).map(branch => ({
        key: branch,
        label: branch
      }))
    } catch {
      return []
    }
  }, [selectedRepoKey, getBranches])

  const repositoryItems = useMemo(
    () =>
      selectableRepos.map(repo => ({
        key: buildRepositoryItemKey(repo),
        label: repo.index_name
      })),
    [selectableRepos]
  )

  const computeRuntimeSettings = useCallback(
    (repoName: string, branchName: string): Record<string, string | undefined> => {
      const orgRuntimeSetting = currentWorkspace?.org_runtime_setting

      for (const index of indexes) {
        if (index.index_name === repoName && index.branch_name === branchName) {
          const indexRuntimeSettings = index.indexer_metadata?.runtime_setting
          return {
            javaVersion: indexRuntimeSettings?.java_version || '',
            mavenVersion: indexRuntimeSettings?.maven_version || '',
            muleRuntimeVersion: indexRuntimeSettings?.mule_runtime_version || '',
            settingsXmlRawTxt:
              indexRuntimeSettings?.settings_xml_raw_txt ||
              orgRuntimeSetting?.settings_xml_raw_txt ||
              '',
            settingsSecurityXmlRawTxt:
              indexRuntimeSettings?.settings_security_xml_raw_txt ||
              orgRuntimeSetting?.settings_security_xml_raw_txt ||
              '',
            vm_args: indexRuntimeSettings?.vm_args || ''
          }
        }
      }

      return {
        javaVersion: '',
        mavenVersion: '',
        muleRuntimeVersion: '',
        settingsXmlRawTxt: orgRuntimeSetting?.settings_xml_raw_txt || '',
        settingsSecurityXmlRawTxt: orgRuntimeSetting?.settings_security_xml_raw_txt || '',
        vm_args: ''
      }
    },
    [currentWorkspace?.org_runtime_setting, indexes]
  )

  const handleRepoSelectionChange = useCallback(
    (value: string) => {
      if (!value) {
        setSelectedRepoKey(null)
        setSelectedBranch('')
        return
      }
      setSelectedRepoKey(value)
      try {
        const { index_name, conn_type } = JSON.parse(value) as {
          index_name: string
          conn_type: string
        }
        const [firstBranch] = getBranches(conn_type, index_name)
        const branchToUse = firstBranch || ''
        setSelectedBranch(branchToUse)
        if (branchToUse) {
          setPendingRuntimeSettings(computeRuntimeSettings(index_name, branchToUse))
        }
      } catch {
        setSelectedBranch('')
      }
    },
    [computeRuntimeSettings, getBranches]
  )

  const handleBranchSelectionChange = useCallback(
    (value: string) => {
      if (!value) {
        setSelectedBranch('')
        return
      }
      setSelectedBranch(value)
      if (selectedRepoKey) {
        try {
          const { index_name } = JSON.parse(selectedRepoKey) as { index_name: string }
          setPendingRuntimeSettings(computeRuntimeSettings(index_name, value))
        } catch {
          // ignore parse errors
        }
      }
    },
    [computeRuntimeSettings, selectedRepoKey]
  )

  const handleUploadComplete = useCallback((path: string) => {
    setUploadedPath(path)
    setUploadState(prev => ({
      ...prev,
      singleRepo: {
        name: path.split('/').pop() || 'uploaded-project',
        s3Path: path,
        status: 'completed'
      },
      isReady: true
    }))
  }, [])

  const { pickFolder } = useFolderPicker()

  const handleIdeProjectSelect = useCallback((path: string) => {
    setSelectedIdePath(prev => (prev === path ? null : path))
    setIdeUploadedPath('')
  }, [])

  const handleIdeWorkspaceUpload = useCallback(async () => {
    if (!isInPlugin || isIdeUploading || !selectedIdePath) return
    const project = pluginWs?.projects?.find(p => p.path === selectedIdePath)
    if (!project) return

    setIsIdeUploading(true)
    try {
      const uploadResult = await projectService.uploadProject(
        project.path,
        project.name || 'workspace',
        currentWorkspace?.uuid
      )

      if (!uploadResult.success || !uploadResult.s3Path) {
        BetterToast.error(uploadResult.error || `Failed to upload ${project.name}`)
        return
      }

      setIdeUploadedPath(uploadResult.s3Path)
    } catch (error) {
      BetterToast.error(
        `Failed to upload IDE workspace: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsIdeUploading(false)
    }
  }, [isInPlugin, isIdeUploading, selectedIdePath, pluginWs?.projects, currentWorkspace?.uuid])

  const handleFolderUpload = async () => {
    try {
      setUploadState({ status: 'uploading' })
      const result = await pickFolder('Select a repository folder')
      if (!result) {
        setUploadState(defaultUploadState)
        return
      }

      const { tarFile, folderName } = result

      if (!currentWorkspace?.uuid) {
        throw new Error('Workspace UUID not available')
      }
      uploadOrgFile(currentWorkspace.uuid, tarFile, {
        onComplete: (s3Path: string) => {
          handleUploadComplete(s3Path)
          setUploadState({
            status: 'uploaded',
            file: { name: folderName, size: tarFile.size }
          })
        },
        onError: (error: string) => {
          console.error('Upload error:', error)
          setUploadState(defaultUploadState)
          BetterToast.error('Failed to upload repository')
        }
      })
    } catch (error) {
      console.error('Error processing folder:', error)
      setUploadState(defaultUploadState)
      const errorMessage =
        error instanceof Error && error.message.toLowerCase().includes('empty')
          ? error.message
          : 'Failed to process repository'
      BetterToast.error(errorMessage)
    }
  }

  const isSaveDisabled = useMemo(() => {
    if (activeTab === 'ide') {
      return !ideUploadedPath
    }
    if (activeTab === 'upload') {
      return !uploadedPath
    }
    return !(selectedRepoKey && selectedBranch)
  }, [activeTab, ideUploadedPath, selectedBranch, selectedRepoKey, uploadedPath])

  const handleSave = () => {
    if (activeTab === 'ide') {
      if (!ideUploadedPath) return
      onSave({
        option: 'upload',
        s3Path: ideUploadedPath
      })
    } else if (activeTab === 'upload') {
      if (!uploadedPath) {
        return
      }
      onSave({
        option: 'upload',
        s3Path: uploadedPath
      })
    } else if (selectedRepoKey && selectedBranch) {
      try {
        const { index_name, conn_type } = JSON.parse(selectedRepoKey) as {
          index_name: string
          conn_type: string
        }
        onSave({
          option: 'codeContext',
          repository: {
            conn_type,
            repository: index_name,
            branch: selectedBranch
          },
          runtimeSettings: pendingRuntimeSettings
        })
      } catch {
        // ignore invalid save attempts
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className='w-[640px] !max-h-[629px] max-w-[calc(100vw-24px)] rounded-[28px]'
      disableOutsideClick
      data-name='Modal/operation'
    >
      <ModalBody
        className='p-0 flex flex-col gap-[48px] overflow-y-auto h-[561px]'
        data-component='modal-body'
        data-node-id='674:60443'
      >
        {/* Modal Header - node 674:60444 */}
        <div
          className='box-border flex flex-col items-end pt-[60px] pb-0 w-full shrink-0'
          data-component='modal-header'
          data-node-id='674:60444'
        >
          {/* Header Content - node I674:60444;797:939 */}
          <div
            className='flex flex-col items-center gap-[16px] w-full shrink-0'
            data-component='modal-header-content'
            data-node-id='I674:60444;797:939'
          >
            {/* Title - node I674:60444;797:941 */}
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='text-black text-center shrink-0 select-none'
              style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
              data-component='modal-title'
              data-node-id='I674:60444;797:941'
            >
              Select Repository
            </Typography>
            {/* Description - node I674:60444;797:942 */}
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-[rgba(0,0,0,0.45)] text-center w-[360px] shrink-0 select-none'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
              data-component='modal-subtitle'
              data-node-id='I674:60444;797:942'
            >
              Select the repository and branch you&apos;d like to test
            </Typography>
          </div>
        </div>

        {/* Modal Body Section - node 674:60445 */}
        <div
          className='flex flex-col items-start w-full shrink-0 pb-[70px]'
          data-component='modal-body-section'
          data-node-id='674:60445'
        >
          {/* Segmented Control Container - node 674:60446 */}
          <div
            className='box-border flex flex-col items-center pt-[24px] pb-0 gap-[10px] w-full shrink-0'
            data-node-id='674:60446'
          >
            <SegmentControls
              value={activeTab}
              onValueChange={value => setActiveTab(value as TabValue)}
              data-name='Segmented Control'
              data-node-id='674:60447'
            >
              <SegmentControlsList>
                <SegmentControlsTrigger value='codeContext' disabled={disabled}>
                  Select Repo
                </SegmentControlsTrigger>
                <SegmentControlsTrigger value='upload' disabled={disabled}>
                  Upload Repo
                </SegmentControlsTrigger>
                {isInPlugin && (
                  <SegmentControlsTrigger value='ide' disabled={disabled}>
                    IDE Workspace
                  </SegmentControlsTrigger>
                )}
              </SegmentControlsList>
            </SegmentControls>
          </div>

          {/* Form Fields Container - node 674:60448 */}
          <div
            className='box-border flex flex-col gap-[24px] items-center px-[60px] py-[48px] w-full shrink-0'
            data-component='form-fields-container'
            data-name='Menu list/multiselect'
            data-node-id='674:60448'
          >
            <SegmentControls
              value={activeTab}
              onValueChange={value => setActiveTab(value as TabValue)}
            >
              <SegmentControlsContent
                value='codeContext'
                className='flex flex-col gap-[48px] items-center w-full shrink-0'
                data-node-id='674:60551'
              >
                {/* Fields Wrapper - node 674:60552 */}
                <div
                  className='flex flex-col gap-[16px] items-start shrink-0'
                  data-node-id='674:60552'
                >
                  {/* Repository Field - node 674:60553 */}
                  <Selector
                    variant='labeled'
                    label='Repository Name'
                    placeholder='Select Repositories...'
                    value={selectedRepoKey || ''}
                    onSelectionChange={handleRepoSelectionChange}
                    disabled={disabled || repositoryItems.length == 0}
                    disabledText='No Repositories...'
                    className='w-[480px] h-[48px]'
                    triggerClassName='h-[48px] rounded-[12px]'
                    valueClassName='font-medium text-[14px]'
                    dropdownClassName='max-h-[240px] overflow-y-auto'
                    data-component='repository-field-wrapper'
                    data-node-id='674:60553'
                  >
                    {repositoryItems.map(item => (
                      <SelectItem key={item.key} value={item.key}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </Selector>

                  {/* Branch Field - node 674:60558 */}
                  <Selector
                    variant='labeled'
                    label='Branch Name'
                    placeholder='Select Branches...'
                    value={selectedBranch || ''}
                    onSelectionChange={handleBranchSelectionChange}
                    disabled={disabled || !selectedRepoKey}
                    disabledText='No Branches...'
                    className='w-[480px] h-[48px]'
                    triggerClassName='h-[48px] rounded-[12px]'
                    valueClassName='font-medium text-[14px]'
                    dropdownClassName='max-h-[300px] overflow-y-auto'
                    data-component='branch-field-wrapper'
                    data-node-id='674:60558'
                  >
                    {branchItems.map(item => (
                      <SelectItem key={item.key} value={item.key}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </Selector>
                </div>
              </SegmentControlsContent>

              <SegmentControlsContent
                value='upload'
                className='flex items-center justify-center w-full'
              >
                <div
                  className={cm('w-full', disabled && 'opacity-70 pointer-events-none')}
                  data-component='upload-area-wrapper'
                  onClick={handleFolderUpload}
                  role='button'
                >
                  <UploadFile
                    variant='without-toggle'
                    uploadState={uploadState}
                    defaultIcon={<XmlFileUploadLeftIcon />}
                    uploadedIcon={<CodeFileIcon />}
                    mainTitle='Upload your repository'
                    description='Click or drag and drop your file with max size of 5 MB'
                    onDropFile={async file => {
                      try {
                        setUploadState({ status: 'uploading' })
                        const fileList = [file] as unknown as FileList
                        const { tarFile, originalFilename } = await createCompressedTarArchive(
                          fileList,
                          '',
                          false
                        )
                        if (!currentWorkspace?.uuid) {
                          throw new Error('Workspace UUID not available')
                        }
                        uploadOrgFile(currentWorkspace.uuid, tarFile, {
                          onComplete: (s3Path: string) => {
                            handleUploadComplete(s3Path)
                            const displayName = originalFilename.replace(/\.tar(\.gz)?$/, '')
                            setUploadState({
                              status: 'uploaded',
                              file: { name: displayName, size: tarFile.size }
                            })
                          },
                          onError: error => {
                            console.error('Upload error:', error)
                            setUploadState(defaultUploadState)
                            BetterToast.error('Failed to upload repository')
                          }
                        })
                      } catch (error) {
                        console.error('Error creating tar archive:', error)
                        setUploadState(defaultUploadState)
                        const errorMessage =
                          error instanceof Error && error.message.toLowerCase().includes('empty')
                            ? error.message
                            : 'Failed to process repository'
                        BetterToast.error(errorMessage)
                      }
                    }}
                    onRemove={() => {
                      setUploadedPath('')
                      setUploadState(defaultUploadState)
                    }}
                    disabled={disabled}
                    inputId='repositoryFolderUpload'
                  />
                </div>
              </SegmentControlsContent>

              {isInPlugin && (
                <SegmentControlsContent
                  value='ide'
                  className='flex items-center justify-center w-full'
                >
                  <div className='w-full rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-3'>
                    <div className='flex flex-col gap-1'>
                      {!pluginWs?.projects || pluginWs.projects.length === 0 ? (
                        <Typography
                          fontFamily='geist'
                          fontSize='bodyMd'
                          fontWeight='normal'
                          className='text-[rgba(0,0,0,0.45)] py-4 text-center'
                        >
                          No projects found in the IDE workspace.
                        </Typography>
                      ) : isIdeUploading ? (
                        <div className='flex items-center justify-center gap-2 py-4'>
                          <svg
                            className='w-4 h-4 animate-spin text-[rgba(0,0,0,0.45)]'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                          >
                            <circle
                              className='opacity-25'
                              cx='12'
                              cy='12'
                              r='10'
                              stroke='currentColor'
                              strokeWidth='4'
                            />
                            <path
                              className='opacity-75'
                              fill='currentColor'
                              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                            />
                          </svg>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMd'
                            fontWeight='normal'
                            className='text-[rgba(0,0,0,0.45)]'
                          >
                            Uploading project...
                          </Typography>
                        </div>
                      ) : (
                        <>
                          {pluginWs.projects.map(project => {
                            const isSelected = selectedIdePath === project.path
                            const isUploaded = ideUploadedPath && isSelected
                            return (
                              <button
                                key={project.path}
                                type='button'
                                onClick={() => handleIdeProjectSelect(project.path)}
                                disabled={disabled}
                                className={cm(
                                  'flex items-center gap-2.5 w-full rounded-lg px-3 py-2.5 text-left transition-colors',
                                  isUploaded
                                    ? 'bg-[rgba(159,159,248,0.2)]'
                                    : isSelected
                                      ? 'bg-[rgba(159,159,248,0.1)]'
                                      : 'hover:bg-[rgba(0,0,0,0.04)]'
                                )}
                              >
                                {isUploaded ? (
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 20 20'
                                    fill='currentColor'
                                    className='w-4 h-4 flex-shrink-0 text-green-600'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                                      clipRule='evenodd'
                                    />
                                  </svg>
                                ) : (
                                  <div
                                    className={cm(
                                      'w-4 h-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-colors',
                                      isSelected ? 'border-[#65558F]' : 'border-[#D6D6D6]'
                                    )}
                                  >
                                    {isSelected && (
                                      <div className='w-2 h-2 rounded-full bg-[#65558F]' />
                                    )}
                                  </div>
                                )}
                                <div className='min-w-0'>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='bodyMd'
                                    fontWeight='medium'
                                    className='text-black truncate'
                                  >
                                    {project.name}
                                  </Typography>
                                  {isUploaded && (
                                    <Typography
                                      fontFamily='geist'
                                      fontSize='labelSm'
                                      fontWeight='normal'
                                      className='text-[rgba(0,0,0,0.45)]'
                                    >
                                      Uploaded from IDE
                                    </Typography>
                                  )}
                                </div>
                              </button>
                            )
                          })}
                          {selectedIdePath && !ideUploadedPath && (
                            <button
                              type='button'
                              onClick={handleIdeWorkspaceUpload}
                              disabled={disabled || isIdeUploading}
                              className='mt-2 w-full h-9 rounded-lg border border-[#D6D6D6] text-sm font-medium text-black hover:bg-[rgba(0,0,0,0.04)] transition-colors disabled:opacity-50'
                            >
                              Upload Project
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </SegmentControlsContent>
              )}
            </SegmentControls>
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        className='bg-[#FCFCFC] border-t border-[#EAEAEA] flex gap-[8px] items-start justify-end px-[32px] py-[16px] shrink-0 w-full'
        data-name='Footer control'
        data-node-id='674:60563'
      >
        <Button
          variant='outlined'
          onClick={onClose}
          className='rounded-[12px] px-[16px] py-[8px] h-[36px] text-sm border border-[#d6d6d6]'
          data-node-id='I674:60563;797:963'
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleSave}
          className='rounded-[12px] px-[16px] py-[8px] h-[36px] text-sm'
          isDisabled={isSaveDisabled || disabled}
          data-node-id='I674:60563;1063:15619'
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default IntegrationRepoSelectionModal
